import React, { Dispatch, SetStateAction, useEffect, useMemo } from "react";
// @ts-ignore
import imagedevice from "src/assets/images/smst.jpg";
// @ts-ignore
import imagedevice2 from "src/assets/images/field.jpg";
// @ts-ignore
import imagedevice3 from "src/assets/images/bigst.jpg";
import { GoArrowRight } from "react-icons/go";
import { useAtom } from "jotai";
import { useTranslation } from "react-i18next";
import { Check } from "lucide-react";
import { itemsAtom, selectedSubscription } from "src/atoms/constantsCheckout";

interface Props {
    step: number;
    setStep: Dispatch<SetStateAction<number>>;
    packages: any[];
}

interface SubscriptionCard {
    id: number;
    title: string;
    price: number;
    currency: string;
    description: string;
    features: string[];
    popular: boolean;
    image: string;
}

export const SuscriptionView = ({ step, setStep, packages }: Props) => {
    const [selectedCard, setSelectedCard] = useAtom(selectedSubscription);
    const [items, setItems] = useAtom(itemsAtom);
    const { t } = useTranslation();

    const clubPackage = packages.find((pkg) => pkg.id === "2");
    const proClubPackage = packages.find((pkg) => pkg.id === "3");
    const eliteClubPackage = packages.find((pkg) => pkg.id === "4");

    const continueButtonClass = useMemo(
        () =>
            "px-8 py-3 rounded-2xl font-semibold flex items-center justify-center space-x-2 transition-all bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white shadow-lg hover:shadow-xl hover:scale-[1.02] disabled:bg-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed disabled:hover:scale-100",
        []
    );

    const cards: SubscriptionCard[] = useMemo(
        () => [
            {
                id: 1,
                title: t("subscriptionView.plans.club.title"),
                price: 30,
                currency: "€",
                description: t("subscriptionView.plans.club.description"),
                features: [
                    t("subscriptionView.plans.club.features.feature_1"),
                    t("subscriptionView.plans.club.features.feature_2"),
                    t("subscriptionView.plans.club.features.feature_3"),
                    t("subscriptionView.plans.club.features.feature_4"),
                    t("subscriptionView.plans.club.features.feature_5"),
                    t("subscriptionView.plans.club.features.feature_6"),
                    t("subscriptionView.plans.club.features.feature_7"),
                ],
                popular: false,
                image: imagedevice,
            },
            {
                id: 2,
                title: t("subscriptionView.plans.proClub.title"),
                price: 60,
                currency: "€",
                description: t("subscriptionView.plans.proClub.description"),
                features: [
                    t("subscriptionView.plans.proClub.features.feature_1"),
                    t("subscriptionView.plans.proClub.features.feature_2"),
                    t("subscriptionView.plans.proClub.features.feature_3"),
                    t("subscriptionView.plans.proClub.features.feature_4"),
                    t("subscriptionView.plans.proClub.features.feature_5"),
                    t("subscriptionView.plans.proClub.features.feature_6"),
                    t("subscriptionView.plans.proClub.features.feature_7"),
                ],
                popular: true,
                image: imagedevice2,
            },
            {
                id: 3,
                title: t("subscriptionView.plans.eliteClub.title"),
                price: 90,
                currency: "€",
                description: t("subscriptionView.plans.eliteClub.description"),
                features: [
                    t("subscriptionView.plans.eliteClub.features.feature_1"),
                    t("subscriptionView.plans.eliteClub.features.feature_2"),
                    t("subscriptionView.plans.eliteClub.features.feature_3"),
                    t("subscriptionView.plans.eliteClub.features.feature_4"),
                    t("subscriptionView.plans.eliteClub.features.feature_5"),
                    t("subscriptionView.plans.eliteClub.features.feature_6"),
                    t("subscriptionView.plans.eliteClub.features.feature_7"),
                ],
                popular: false,
                image: imagedevice3,
            },
        ],
        [t]
    );

    useEffect(() => {
        if (!selectedCard?.title && items.subscriptions?.type !== null && items.subscriptions?.type !== undefined) {
            setSelectedCard({
                title: items.subscriptions.type,
                price: items.subscriptions.price,
                id: "2",
                currency: "€",
                description: "",
                features: [],
                popular: false,
                image: "",
            });
        } else if (!selectedCard?.title) {
            const defaultPlan = cards.find((card) => card.popular);
            if (defaultPlan) {
                setSelectedCard(defaultPlan);
            }
        }
    }, [items.subscriptions, selectedCard?.title, cards, setSelectedCard]);

    const handleContinue = () => {
        if (selectedCard) {
            setStep(step + 1);
            setItems((prev: any) => ({
                ...prev,
                subscriptions: {
                    type: selectedCard.title,
                    price: selectedCard.price,
                },
            }));
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white w-full p-4 md:p-8 lg:p-12">
            <div className="max-w-7xl mx-auto">
                {/* Header Section */}
                <div className="text-center mb-12">
                    <h1 className="text-4xl font-bold text-gray-900 mb-4">
                        {t("subscriptionView.title")}
                    </h1>
                    <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                        {t("subscriptionView.subtitle")}
                    </p>
                </div>

                {/* Pricing Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
                    {cards.map((card) => (
                        <div
                            key={card.id}
                            className={`relative rounded-xl overflow-hidden shadow-lg transition-all duration-300 transform hover:shadow-xl cursor-pointer ${selectedCard?.title === card.title
                                ? "ring-4 ring-purple-500 scale-[1.02] bg-white"
                                : "bg-white"
                                }`}
                            onClick={() => setSelectedCard(card)}
                        >
                            {card.popular && (
                                <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-10">
                                    <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-6 py-2 rounded-full text-sm font-bold shadow-md mt-3">
                                        {t("subscriptionView.plans.proClub.more_popular")}
                                    </div>
                                </div>
                            )}

                            {/* Card Header */}
                            <div className="relative h-48 overflow-hidden">
                                <img
                                    src={card.image}
                                    alt={card.title}
                                    className="w-full h-full object-cover"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                                <div className="absolute bottom-0 left-0 p-6 text-white">
                                    <div>
                                        <h3 className="text-2xl font-bold">{card.title}</h3>
                                    </div>
                                </div>
                            </div>

                            {selectedCard?.title === card.title && (
                                <div className="bg-white rounded-full p-2 shadow-md absolute top-2 right-2 z-20">
                                    <Check size={20} className="text-purple-600" />
                                </div>
                            )}

                            {/* Price Section */}
                            <div className="p-6 border-b border-gray-100">
                                <div className="flex items-end justify-center">
                                    <span className="text-5xl font-bold text-gray-900">
                                        {card.price}
                                    </span>
                                    <span className="text-xl text-gray-500 ml-1">
                                        {card.currency}
                                    </span>
                                    <span className="text-gray-500 ml-1">/mo</span>
                                </div>
                            </div>

                            {/* Features List */}
                            <div className="p-6">
                                <ul className="space-y-3">
                                    {card.features.map((feature, index) => (
                                        <li key={index} className="flex items-start">
                                            <Check
                                                size={18}
                                                className="text-green-500 mt-1 flex-shrink-0"
                                            />
                                            <span className="text-gray-700 ml-3">{feature}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Navigation Buttons */}
                <div className="flex flex-col sm:flex-row justify-center gap-4 mt-8">
                    {step > 1 && (
                        <button
                            onClick={() => setStep(step - 1)}
                            className="px-8 py-3 border border-gray-300 rounded-full font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                        >
                            {t("subscriptionView.buttons.goBack")}
                        </button>
                    )}
                    <button
                        onClick={handleContinue}
                        className={continueButtonClass}
                        disabled={!selectedCard}
                    >
                        <span>{t("subscriptionView.buttons.continue")}</span>
                        <GoArrowRight className="h-5 w-5" />
                    </button>
                </div>
            </div>
        </div>
    );
};
